import React, { useState } from 'react'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  CpuChipIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import {
  useListScoutRunsQuery,
  useGetScoutRunQuery,
  useListScoutModelsQuery,
  ScoutRunRow,
  ScoutStats,
} from '../store/api/aiScoutApi'
import { formatDateTimeEST } from '../utils/dateUtils'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function fmtNum(n: number | string | null | undefined): string {
  if (n == null || n === '') return '—'
  return Number(n).toLocaleString()
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  )
}

function ConfidenceBadge({ level }: { level: string | null }) {
  if (!level) return <span className="text-gray-400 text-xs">—</span>
  const map: Record<string, string> = {
    high:   'bg-green-50 text-green-700 border-green-200',
    medium: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    low:    'bg-red-50 text-red-700 border-red-200',
  }
  const cls = map[level] ?? 'bg-gray-50 text-gray-700 border-gray-200'
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full border ${cls}`}>
      {level}
    </span>
  )
}

function UsedBadge({ wasUsed }: { wasUsed: boolean }) {
  if (wasUsed) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-50 text-green-700 border border-green-200">
        <CheckCircleIcon className="h-3 w-3" /> Used
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-200">
      <ExclamationCircleIcon className="h-3 w-3" /> Failed
    </span>
  )
}

// ─── Detail drawer ─────────────────────────────────────────────────────────────

function ScoutRunDrawer({ uuid, onClose }: { uuid: string; onClose: () => void }) {
  const { data, isLoading } = useGetScoutRunQuery(uuid)
  const run = data?.data

  let parsedAnalysis: Record<string, unknown> | null = null
  if (run?.analysis_json) {
    try { parsedAnalysis = JSON.parse(run.analysis_json) } catch { /* ignore */ }
  }

  const confidence = (parsedAnalysis?.confidenceLevel as string) ?? null

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      {/* Panel */}
      <div className="relative w-full max-w-2xl bg-white shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Scout Run Detail</h2>
            {run && (
              <p className="text-xs text-gray-500 font-mono mt-0.5">{run.uuid}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : !run ? (
            <p className="text-red-600 text-sm">Could not load run.</p>
          ) : (
            <>
              {/* Meta grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block mb-0.5">Status</span>
                  <UsedBadge wasUsed={run.was_used} />
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block mb-0.5">Confidence</span>
                  <ConfidenceBadge level={confidence} />
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block mb-0.5">Model</span>
                  <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{run.model}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block mb-0.5">Duration</span>
                  <span className="text-gray-800">{fmtMs(run.duration_ms)}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block mb-0.5">Prompt tokens</span>
                  <span className="text-gray-800">{fmtNum(run.prompt_tokens)}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block mb-0.5">Completion tokens</span>
                  <span className="text-gray-800">{fmtNum(run.completion_tokens)}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block mb-0.5">Ticket</span>
                  <span className="text-gray-800">{run.ticket_title || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block mb-0.5">Service</span>
                  <span className="text-gray-800">{run.service_name || '—'}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block mb-0.5">Session ID</span>
                  <span className="font-mono text-xs text-gray-500">{run.session_uuid || run.session_id}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-500 uppercase tracking-wide block mb-0.5">Run at</span>
                  <span className="text-gray-800">{formatDateTimeEST(run.created_at)}</span>
                </div>
              </div>

              {/* Error */}
              {run.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-xs font-medium text-red-700 mb-1">Error</p>
                  <p className="text-xs text-red-600 font-mono break-words">{run.error}</p>
                </div>
              )}

              {/* Summary */}
              {run.analysis_summary && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Summary</h3>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 leading-relaxed">
                    {run.analysis_summary}
                  </p>
                </div>
              )}

              {/* Structured analysis */}
              {parsedAnalysis && (
                <div className="space-y-4">
                  <AnalysisSection title="Likely files" items={parsedAnalysis.likelyFiles as string[]} mono />
                  <AnalysisSection title="Relevant modules" items={parsedAnalysis.relevantModules as string[]} />
                  <AnalysisSection title="Implementation steps" items={parsedAnalysis.implementationSteps as string[]} numbered />
                  <AnalysisSection title="Risks" items={parsedAnalysis.risks as string[]} />
                  <AnalysisSection title="Missing context" items={parsedAnalysis.missingContext as string[]} />
                  <AnalysisSection title="Test suggestions" items={parsedAnalysis.testSuggestions as string[]} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function AnalysisSection({
  title,
  items,
  mono = false,
  numbered = false,
}: {
  title: string
  items: string[] | null | undefined
  mono?: boolean
  numbered?: boolean
}) {
  if (!items?.length) return null
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-1.5">{title}</h3>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 text-sm text-gray-600">
            <span className="text-gray-400 shrink-0">{numbered ? `${i + 1}.` : '–'}</span>
            <span className={mono ? 'font-mono text-xs text-gray-700 break-all' : ''}>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─── Stats banner ─────────────────────────────────────────────────────────────

function StatsBanner({ stats }: { stats: ScoutStats }) {
  const successRate = stats.total_runs > 0
    ? Math.round((Number(stats.successful_runs) / Number(stats.total_runs)) * 100)
    : 0

  const confidenceHigh   = Number(stats.high_confidence)
  const confidenceMedium = Number(stats.medium_confidence)
  const confidenceLow    = Number(stats.low_confidence)

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      <StatCard label="Total runs"     value={fmtNum(stats.total_runs)} />
      <StatCard label="Success rate"   value={`${successRate}%`}        sub={`${fmtNum(stats.successful_runs)} used`} />
      <StatCard label="Avg duration"   value={fmtMs(Number(stats.avg_duration_ms))} />
      <StatCard label="Avg tokens (prompt)"     value={fmtNum(stats.avg_prompt_tokens)} sub={`${fmtNum(stats.total_prompt_tokens)} total`} />
      <StatCard label="Avg tokens (completion)" value={fmtNum(stats.avg_completion_tokens)} sub={`${fmtNum(stats.total_completion_tokens)} total`} />
      <StatCard
        label="Confidence"
        value={`H:${confidenceHigh} M:${confidenceMedium} L:${confidenceLow}`}
        sub="high / medium / low"
      />
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

const AiScout: React.FC = () => {
  const [page, setPage]         = useState(1)
  const [search, setSearch]     = useState('')
  const [wasUsed, setWasUsed]   = useState<'' | 'true' | 'false'>('')
  const [modelFilter, setModelFilter] = useState('')
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)

  const { data, isLoading, isFetching, error, refetch } = useListScoutRunsQuery({
    page,
    limit: 50,
    search:  search  || undefined,
    wasUsed: wasUsed || undefined,
    model:   modelFilter || undefined,
  })

  const { data: modelsData } = useListScoutModelsQuery()
  const availableModels = modelsData?.data ?? []

  const rows  = data?.data  ?? []
  const meta  = data?.meta
  const stats = data?.stats

  const handleSearch = (val: string) => {
    setSearch(val)
    setPage(1)
  }
  const handleWasUsed = (val: '' | 'true' | 'false') => {
    setWasUsed(val)
    setPage(1)
  }
  const handleModel = (val: string) => {
    setModelFilter(val)
    setPage(1)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading AI Scout data. Please try again.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Scout</h1>
          <p className="text-gray-500 text-sm mt-1">
            Repo-analysis pre-passes run before the main AI Builder agent.
            Each row is one builder turn where the scout ran.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && <StatsBanner stats={stats} />}

      {/* Filters + table card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">

        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search ticket, service, model, summary…"
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          {/* Status filter */}
          <select
            className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={wasUsed}
            onChange={(e) => handleWasUsed(e.target.value as '' | 'true' | 'false')}
          >
            <option value="">All statuses</option>
            <option value="true">Used (success)</option>
            <option value="false">Failed / skipped</option>
          </select>

          {/* Model filter */}
          <select
            className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={modelFilter}
            onChange={(e) => handleModel(e.target.value)}
          >
            <option value="">All models</option>
            {availableModels.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {/* Count */}
        {meta && (
          <p className="text-xs text-gray-500 mb-3">
            {meta.total.toLocaleString()} run{meta.total !== 1 ? 's' : ''} total
            {(search || wasUsed || modelFilter) && ' (filtered)'}
          </p>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket / Service</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confidence</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span className="inline-flex items-center gap-1"><CpuChipIcon className="h-3.5 w-3.5" />Tokens</span>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span className="inline-flex items-center gap-1"><ClockIcon className="h-3.5 w-3.5" />Duration</span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Summary</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Run at</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-10 text-center text-gray-400">
                    No scout runs found.
                    {!stats?.total_runs && (
                      <span className="block text-xs mt-1 text-gray-400">
                        Runs will appear here after the AI Builder fires its first build turn with the scout enabled.
                      </span>
                    )}
                  </td>
                </tr>
              ) : (
                rows.map((row) => <RunRow key={row.id} row={row} onOpen={() => setSelectedUuid(row.uuid)} />)
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && meta.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-700">
              Showing {((meta.page - 1) * meta.limit + 1).toLocaleString()}–
              {Math.min(meta.page * meta.limit, meta.total).toLocaleString()} of {meta.total.toLocaleString()}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={meta.page <= 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                disabled={meta.page >= meta.totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selectedUuid && (
        <ScoutRunDrawer uuid={selectedUuid} onClose={() => setSelectedUuid(null)} />
      )}
    </div>
  )
}

function RunRow({ row, onOpen }: { row: ScoutRunRow; onOpen: () => void }) {
  let confidence: string | null = null
  if (row.analysis_json) {
    try {
      const parsed = JSON.parse(row.analysis_json)
      confidence = parsed?.confidenceLevel ?? null
    } catch { /* ignore */ }
  }

  const totalTokens = (row.prompt_tokens || 0) + (row.completion_tokens || 0)

  return (
    <tr className="hover:bg-gray-50 cursor-pointer" onClick={onOpen}>
      {/* Ticket / service */}
      <td className="px-4 py-3 max-w-xs">
        <div className="font-medium text-gray-900 truncate">{row.ticket_title || '—'}</div>
        <div className="text-xs text-gray-500 truncate">{row.service_name || '—'}</div>
      </td>
      {/* Model */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{row.model}</span>
      </td>
      {/* Status */}
      <td className="px-4 py-3 whitespace-nowrap">
        <UsedBadge wasUsed={row.was_used} />
      </td>
      {/* Confidence */}
      <td className="px-4 py-3 whitespace-nowrap">
        <ConfidenceBadge level={confidence} />
      </td>
      {/* Tokens */}
      <td className="px-4 py-3 text-right whitespace-nowrap text-xs text-gray-700">
        <span title={`Prompt: ${fmtNum(row.prompt_tokens)} / Completion: ${fmtNum(row.completion_tokens)}`}>
          {fmtNum(totalTokens)}
        </span>
      </td>
      {/* Duration */}
      <td className="px-4 py-3 text-right whitespace-nowrap text-xs text-gray-700">
        {fmtMs(row.duration_ms)}
      </td>
      {/* Summary */}
      <td className="px-4 py-3 max-w-sm">
        <p className="text-xs text-gray-600 truncate" title={row.analysis_summary ?? undefined}>
          {row.error
            ? <span className="text-red-500">{row.error}</span>
            : (row.analysis_summary || <span className="text-gray-400 italic">no summary</span>)
          }
        </p>
      </td>
      {/* Run at */}
      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
        {formatDateTimeEST(row.created_at)}
      </td>
      {/* View */}
      <td className="px-4 py-3 whitespace-nowrap">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen() }}
          className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
        >
          View
        </button>
      </td>
    </tr>
  )
}

export default AiScout
